/**
 * Hooks de datos sobre TanStack Query.
 *
 * Una clave de caché por recurso, declarada en un solo lugar: las
 * invalidaciones apuntan a `claves.*` y no a literales sueltos, que es como en
 * la v1 se acumulaban listas que no se refrescaban tras una escritura.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'
import type {
  CategoriaPublica,
  CiudadOrigenDto,
  ConfigPublica,
  EntradaEditarProducto,
  EntradaCrearSolicitud,
  EntradaEntrar,
  EntradaImportarCatalogo,
  EntradaMovimientoProveedor,
  EntradaPublicarProducto,
  EntradaRegistro,
  EstadoProveedorDto,
  EstadoSolicitudDto,
  DocumentosArticulo,
  OperadorElegible,
  CuentaEmpresa,
  EntradaCambiarCuenta,
  PaginaNotificaciones,
  SugerenciaCatalogo,
  ItemCatalogo,
  ItemPedidoDto,
  OrdenCatalogo,
  AprobacionClienteDto,
  PaginaCatalogo,
  RespuestaProveedorDto,
  ResultadoImportacion,
  TipoMovimientoProveedor,
  Yo,
  esquemaRecuperar,
  esquemaRestablecer,
  esquemaVerificar,
} from '@compranorte/contracts'
import type { DetalleFlete, ModoDespacho, TipoArchivo } from '@compranorte/domain'

/**
 * El detalle de flete TAL COMO LLEGA POR LA RED.
 *
 * No es `DetalleFlete` del dominio. El servidor le quita `proveedorIds` a quien
 * no es del equipo —eran los ids reales de los distribuidores, y el modelo de
 * reventa no los publica—, así que en el navegador ese campo es opcional. El
 * tipo lo dice para que la pantalla tenga que preguntar antes de leerlo, en vez
 * de confiar en un campo que la mitad de las veces no está.
 */
export type GrupoFleteRecibido = Omit<DetalleFlete['grupos'][number], 'proveedorIds'> & {
  proveedorIds?: string[]
}

export type DetalleFleteRecibido = Omit<DetalleFlete, 'grupos'> & {
  grupos: GrupoFleteRecibido[]
}
import type { EstadoOrdenDto } from '../lib/estados'
import { ErrorApi, pedir } from './cliente'

/** Los endpoints que solo confirman devuelven esto y nada más. */
export interface RespuestaMensaje {
  mensaje: string
}

/*
 * Las entradas se derivan de los esquemas compartidos en vez de redeclararse:
 * si mañana `restablecer` pide algo más, esto deja de compilar.
 */
type EntradaRecuperar = ReturnType<typeof esquemaRecuperar.parse>
type EntradaRestablecer = ReturnType<typeof esquemaRestablecer.parse>
type EntradaVerificar = ReturnType<typeof esquemaVerificar.parse>

export const claves = {
  yo: ['yo'] as const,
  categorias: ['categorias'] as const,
  /*
   * Los filtros entran EN la clave: cambiar de categoría o de orden es otra
   * consulta, con su propia paginación por cursor. Compartir una sola clave
   * obligaría a limpiar las páginas a mano y a arrastrar cursores de una
   * consulta a otra, que es justo lo que el backend rechaza con
   * "El cursor no corresponde a esta consulta".
   */
  catalogo: (filtros: FiltrosCatalogo) => ['catalogo', filtros] as const,
  /** Sugerencias del buscador de la portada, por texto tecleado. */
  sugerencias: (q: string) => ['sugerencias', q] as const,
  producto: (id: string, faenaId?: string | null) => ['producto', id, faenaId ?? null] as const,
  cotizacion: (faenaId: string | null, items: ItemPedidoDto[]) =>
    ['cotizacion', faenaId, items] as const,
  /** Las faenas que uno puede elegir como destino. */
  faenas: ['faenas'] as const,
  ordenes: (estado?: EstadoOrdenDto) => ['ordenes', estado ?? null] as const,
  /*
   * Las líneas cuelgan de la clave de la orden a propósito: invalidar
   * `['orden', id]` alcanza también a `['orden', id, 'lineas']`, así que tras
   * cancelar no queda una tabla de líneas con el estado anterior.
   */
  orden: (id: string) => ['orden', id] as const,
  lineasOrden: (id: string) => ['orden', id, 'lineas'] as const,
  /**
   * Configuración pública del entorno. Se pide en tiempo de ejecución y no se
   * hornea en el bundle, así que la misma imagen sirve para cualquier entorno.
   */
  config: ['config'] as const,
  /** Fichas de proveedor para el selector del equipo. */
  proveedores: ['proveedores'] as const,
  /*
   * El proveedor en contexto entra EN la clave del panel, igual que los filtros
   * del catálogo: cuando el equipo cambia de proveedor es otra consulta, con su
   * propia paginación por cursor. Compartir una clave arrastraría el cursor de
   * un proveedor a la consulta de otro.
   */
  panelProductos: (proveedorId: string | null) => ['panel', 'productos', proveedorId] as const,
  panelOrdenes: (proveedorId: string | null) => ['panel', 'ordenes', proveedorId] as const,
  /** Los archivos cuelgan del producto: borrar uno invalida solo esa lista. */
  archivos: (productoId: string) => ['archivos', productoId] as const,
  /**
   * La documentación que ve el CLIENTE cuelga del artículo, no de la oferta.
   *
   * Por eso es una clave aparte de `archivos`: son las mismas filas vistas
   * desde el otro lado del mostrador —sin nombre original, sin `productoId` y
   * juntando las ofertas de todo el artículo—, y publicar u ocultar un archivo
   * tiene que invalidar las dos.
   */
  documentos: (articuloId: string) => ['documentos', articuloId] as const,
  /** Lo que uno mismo pidió y no estaba en el catálogo. */
  solicitudes: ['solicitudes'] as const,
  /** Los operadores logísticos que el cliente tiene habilitados. */
  operadores: ['operadores'] as const,
  /** Las cuentas de la propia empresa, para su administrador. */
  cuentasEmpresa: ['empresa', 'cuentas'] as const,
  /** La campana. Se refresca sola: es lo único que se consulta en segundo plano. */
  notificaciones: ['notificaciones'] as const,
  /** Sugerencias del asistente, por texto consultado. */
  asistente: (q: string) => ['asistente', q] as const,
}

/**
 * Marca de que existe una sesión, puesta por el backend al entrar.
 *
 * No es httpOnly y no lleva nada dentro: solo un `1`. Sirve para NO preguntar
 * por el perfil cuando no hay sesión. Sin esto, todo visitante anónimo dispara
 * `GET /api/auth/yo`, recibe 401, el cliente intenta refrescar, recibe otro
 * 401, y deja dos errores rojos en la consola y dos peticiones inútiles en la
 * página de más tráfico del sitio.
 *
 * Que se pueda falsificar no importa: lo único que consigue quien la ponga a
 * mano es provocar una consulta que responderá 401. La autorización sigue
 * viviendo en la cookie firmada, que el JavaScript no puede leer.
 */
function hayMarcaDeSesion(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.split('; ').some((c) => c.startsWith('cn_sesion='))
}

/**
 * El perfil propio. Es la consulta que sostiene la sesión entera.
 *
 * `retry: false` es deliberado: un 401 o un 403 no se arreglan repitiendo la
 * petición, y el cliente HTTP ya intentó refrescar la sesión por su cuenta
 * antes de dejar pasar el error. Reintentar solo retrasaría la pantalla de
 * login tres veces.
 */
export function useYo(): UseQueryResult<Yo, ErrorApi> {
  return useQuery<Yo, ErrorApi>({
    queryKey: claves.yo,
    queryFn: () => pedir<Yo>('/api/auth/yo'),
    // Un anónimo no pregunta. Ver `hayMarcaDeSesion`.
    enabled: hayMarcaDeSesion(),
    retry: false,
    staleTime: 60_000,
    // Volver a la pestaña no debería recargar el perfil: cambia cuando un
    // admin toca la cuenta, no cada vez que alguien alterna de ventana.
    refetchOnWindowFocus: false,
  })
}

/**
 * Entrar. La respuesta ES el perfil, así que se siembra en la caché en vez de
 * invalidar y pedirlo otra vez: la pantalla siguiente ya tiene su sesión sin
 * un segundo viaje.
 */
export function useEntrar(): UseMutationResult<Yo, ErrorApi, EntradaEntrar> {
  const qc = useQueryClient()
  return useMutation<Yo, ErrorApi, EntradaEntrar>({
    mutationFn: (datos) => pedir<Yo>('/api/auth/entrar', { metodo: 'POST', cuerpo: datos }),
    onSuccess: (yo) => {
      qc.setQueryData(claves.yo, yo)
      /*
       * Todo lo DEMÁS en la caché se pidió como visitante o como otra persona,
       * así que se invalida. El perfil se excluye a propósito: acaba de llegar
       * en la respuesta, e invalidarlo pediría de inmediato lo que ya se tiene.
       */
      void qc.invalidateQueries({
        predicate: (consulta) => consulta.queryKey[0] !== claves.yo[0],
      })
    },
  })
}

/** Registro. No abre sesión: hay que confirmar el correo primero. */
export function useRegistrar(): UseMutationResult<RespuestaMensaje, ErrorApi, EntradaRegistro> {
  return useMutation<RespuestaMensaje, ErrorApi, EntradaRegistro>({
    mutationFn: (datos) =>
      pedir<RespuestaMensaje>('/api/auth/registro', { metodo: 'POST', cuerpo: datos }),
  })
}

/**
 * Salir.
 *
 * La caché se vacía entera, no solo el perfil: cualquier cosa que quedara
 * dentro —órdenes, faenas, catálogo con precios de cliente— pertenece a la
 * sesión que se acaba de cerrar y no debe ser legible por quien entre después
 * en el mismo navegador.
 *
 * Se limpia igual si el servidor falla: la cookie puede sobrevivir, pero dejar
 * los datos del anterior en pantalla es peor.
 */
export function useSalir(): UseMutationResult<void, ErrorApi, void> {
  const qc = useQueryClient()
  return useMutation<void, ErrorApi, void>({
    mutationFn: () => pedir<void>('/api/auth/salir', { metodo: 'POST' }),
    onSettled: () => {
      /*
       * `removeQueries()` y NO `qc.clear()`, que era lo que había.
       *
       * `clear()` vacía la caché de consultas Y la de MUTACIONES, y la mutación
       * que se está ejecutando en ese momento es justamente esta: se destruía a
       * sí misma en mitad de su propia cadena de callbacks. La consecuencia era
       * que el segundo `onSettled` —el que `Layout` pasa a `mutate()` para
       * navegar a /entrar— no llegaba a correr nunca: la sesión se cerraba de
       * verdad en el servidor, pero la pantalla se quedaba donde estaba y había
       * que recargar a mano.
       *
       * `removeQueries()` borra igual todos los datos cacheados, que es el
       * objetivo —que la cuenta siguiente no vea nada de la anterior— sin tocar
       * la mutación en curso.
       */
      qc.removeQueries()
    },
  })
}

/** Pide el enlace para crear una contraseña nueva. */
export function useRecuperar(): UseMutationResult<RespuestaMensaje, ErrorApi, EntradaRecuperar> {
  return useMutation<RespuestaMensaje, ErrorApi, EntradaRecuperar>({
    mutationFn: (datos) =>
      pedir<RespuestaMensaje>('/api/auth/recuperar', { metodo: 'POST', cuerpo: datos }),
  })
}

/**
 * Fija la contraseña nueva. El backend revoca todas las sesiones al hacerlo,
 * así que la caché local se vacía: lo que hubiera ahí ya no está autorizado.
 */
export function useRestablecer(): UseMutationResult<
  RespuestaMensaje,
  ErrorApi,
  EntradaRestablecer
> {
  const qc = useQueryClient()
  return useMutation<RespuestaMensaje, ErrorApi, EntradaRestablecer>({
    mutationFn: (datos) =>
      pedir<RespuestaMensaje>('/api/auth/restablecer', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      // Mismo motivo que en `useSalir`: `clear()` también vacía la caché de
      // mutaciones, y acá se llevaba por delante el `isSuccess` de esta misma
      // mutación — que es de lo que depende la pantalla de "contraseña
      // cambiada". Se borran las consultas y nada más.
      qc.removeQueries()
    },
  })
}

/**
 * Confirma la dirección de correo.
 *
 * Invalida el perfil porque `correoVerificado` cambia, y de eso depende que un
 * admin pueda asignarle rol: quien llegue acá con sesión abierta debe ver el
 * estado nuevo sin recargar.
 */
export function useVerificarCorreo(): UseMutationResult<
  RespuestaMensaje,
  ErrorApi,
  EntradaVerificar
> {
  const qc = useQueryClient()
  return useMutation<RespuestaMensaje, ErrorApi, EntradaVerificar>({
    mutationFn: (datos) =>
      pedir<RespuestaMensaje>('/api/auth/verificar-correo', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.yo })
    },
  })
}

/**
 * Reenvía el enlace de verificación. Es la salida de emergencia de la pantalla
 * de confirmación: un enlace expirado o ya usado deja a la persona sin camino
 * si no puede pedir otro.
 */
export function useReenviarVerificacion(): UseMutationResult<
  RespuestaMensaje,
  ErrorApi,
  EntradaRecuperar
> {
  return useMutation<RespuestaMensaje, ErrorApi, EntradaRecuperar>({
    mutationFn: (datos) =>
      pedir<RespuestaMensaje>('/api/auth/reenviar-verificacion', {
        metodo: 'POST',
        cuerpo: datos,
      }),
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Catálogo, carrito y órdenes
 *
 * Los tipos de ENTRADA vienen de `@compranorte/contracts` (`ItemPedidoDto`,
 * `ConsultaCatalogo`, …) y los de SALIDA del catálogo también (`ItemCatalogo`,
 * `CategoriaPublica`, `PaginaCatalogo`).
 *
 * Las respuestas de carrito y órdenes NO están en `contracts`: ese paquete
 * declara los esquemas de lo que la API acepta, y las proyecciones de salida de
 * `/api/ordenes` las arma el módulo del servidor campo por campo según quién
 * pregunta. Se declaran acá, en el borde, con los nombres exactos de esa
 * proyección y reutilizando `DetalleFlete` y `ModoDespacho` del dominio: si el
 * motor de flete cambia de forma, esto deja de compilar.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Página de cursor: la forma que devuelve todo listado. */
export interface Pagina<T> {
  datos: T[]
  siguienteCursor: string | null
}

/** Cifras internas de una cotización. Solo llegan si quien pregunta es equipo. */
export interface InternoCotizacion {
  costoProveedores: number
  fleteRealEstimado: number
  costoFactoring: number
  margenContribucion: number
}

export interface Cotizacion {
  subtotalNeto: number
  flete: number
  iva: number
  total: number
  modoDespacho: ModoDespacho
  detalleFlete: DetalleFleteRecibido
  /** Días hábiles del grupo más lento: la orden llega con la última caja. */
  diasEntrega: number
  unidades: number
  /**
   * A cuántos distribuidores se le compra. SOLO LLEGA AL EQUIPO: para el cliente
   * el proveedor no existe, y un contador de proveedores en su pantalla sería
   * decirle cuántos hay detrás.
   */
  proveedoresDistintos?: number
  /** Cargo por pedido bajo el mínimo. Cero cuando no aplica. */
  feePedidoPequeno: number
  /** Líneas que no se pudieron surtir, con el motivo de cada una. */
  faltantes: LineaNoSurtible[]
  interno?: InternoCotizacion
}

/**
 * Una línea del carrito que no se pudo surtir.
 *
 * `sin_stock` y `no_disponible` llevan a decisiones opuestas —bajar la cantidad
 * o quitar la línea—, así que viajan separados en vez de como un solo "no
 * disponible" que obligue a adivinar. `disponible` es el saldo de la mejor
 * oferta: lo que se puede entregar en una línea.
 */
export interface LineaNoSurtible {
  articuloId: string
  nombre: string
  disponible: number
  motivo: 'sin_stock' | 'no_disponible'
}

/** Una faena de destino, tal como la devuelve `GET /api/faenas`. */
export interface Faena {
  id: string
  nombre: string
  ciudad: string
  region: string
  zonaFlete: string
  clienteId: string
  clienteRazonSocial: string | null
}

/** Cifras internas de una orden. Nombres distintos a los de la cotización. */
export interface InternoOrden {
  costoTotalProveedores: number
  fleteRealEstimado: number
  costoFactoring: number
  margenContribucion: number
}

export interface Orden {
  id: string
  numero: string
  clienteId: string
  clienteRazonSocial: string | null
  faenaId: string
  faenaNombre: string | null
  /**
   * A qué andén se entregó la carga. `null` en las órdenes anteriores a que se
   * pudiera elegir, y en las de clientes sin operadores habilitados: no es un
   * dato que falte cargar, es que no hubo operador.
   */
  operadorLogisticoId: string | null
  operadorNombre: string | null
  operadorCiudad: string | null
  fecha: string
  estado: EstadoOrdenDto
  modoDespacho: ModoDespacho
  subtotalNeto: number
  flete: number
  iva: number
  total: number
  diasPago: number
  venceEn: string
  fechaComprometida: string | null
  entregadaEn: string | null
  pagadaEn: string | null
  motivoCancelacion: string | null
  requirioCotizacion: boolean

  /** En qué va el visto bueno del cliente sobre lo que el equipo le propuso. */
  aprobacionCliente: AprobacionClienteDto
  rondaAjuste: number
  /** Qué ajustó el equipo y por qué. Lo escribe quien revisa. */
  notaAjuste: string | null
  /** Hasta cuándo vale la propuesta. Al vencer, la orden se cancela sola. */
  propuestaVenceEn: string | null
  /** Fecha de entrega propuesta, si el equipo mueve la comprometida. */
  fechaComprometidaPropuesta: string | null
  aprobadaEn: string | null
  motivoRechazoCliente: string | null

  detalleFlete: DetalleFleteRecibido
  interno?: InternoOrden
}

export interface EventoOrden {
  estadoAnterior: EstadoOrdenDto | null
  estadoNuevo: EstadoOrdenDto
  nota: string | null
  creadoEn: string
}

/** La ficha trae además la bitácora de transiciones. */
export interface OrdenConEventos extends Orden {
  eventos: EventoOrden[]
}

/**
 * Una línea de orden, tal como la proyecta el servidor.
 *
 * Los campos opcionales no son un descuido: dependen de QUIÉN pregunta, y el
 * tipo lo refleja para que la pantalla tenga que decidir qué hacer cuando no
 * están, en vez de renderizar `undefined`.
 *
 *  * `codigo`/`articuloId` los reciben el cliente y el equipo: es el artículo
 *    que se pidió, con nuestro código.
 *  * `sku`/`productoId` los recibe el proveedor —lo que tiene que sacar de la
 *    estantería— y el equipo. Un cliente NO: sería el id de una oferta que no
 *    debería poder nombrar.
 *  * `proveedorId`/`proveedorRazonSocial` son solo del equipo.
 */
export interface LineaOrden {
  id: string
  nombre: string
  marca: string
  imagenUrl: string | null
  /** Ciudad desde la que sale la línea. Explica el plazo, y no identifica a nadie. */
  ciudadOrigen: string
  cantidad: number
  cantidadDespachada: number
  estadoLinea: EstadoOrdenDto

  /**
   * El precio de lista, antes de cualquier ajuste concedido. Junto a
   * `precioUnitario` muestra el descuento. No es el costo: eso no sale de acá.
   */
  precioCalculado: number
  /** Lo que el equipo propone y el cliente todavía no aprueba. Nulo = sin cambio. */
  cantidadPropuesta: number | null
  precioPropuesto: number | null

  /** Cliente y equipo: el artículo pedido. */
  articuloId?: string
  codigo?: string

  /** Proveedor y equipo: la oferta concreta. */
  productoId?: string
  sku?: string

  /** Solo el equipo. */
  proveedorId?: string
  proveedorRazonSocial?: string

  /*
   * La respuesta del distribuidor a su OC. OPCIONALES porque el servidor no se
   * las manda al cliente: para él, la existencia misma de un proveedor no debe
   * asomar. El tipo obliga a preguntar antes de leerlas.
   */
  respuestaProveedor?: RespuestaProveedorDto
  cantidadConfirmada?: number | null
  motivoRechazo?: string | null
  respondidaEn?: string | null

  /** Lo ve el cliente y el equipo; un proveedor no. */
  precioUnitario?: number
  /** Solo el equipo y el proveedor dueño de la línea. */
  costoUnitario?: number
}

/** Lo que devuelve la emisión: el acuse, no la orden completa. */
export interface OrdenEmitida {
  id: string
  numero: string
  fecha: string
  venceEn: string
  fechaComprometida: string
  total: number
}

/** Filtros del catálogo tal como los maneja la pantalla. */
export interface FiltrosCatalogo {
  categoria?: string
  marca?: string
  proveedorId?: string
  q?: string
  orden?: OrdenCatalogo
  /** Faena en contexto: fija la zona con la que se calcula el plazo mostrado. */
  faenaId?: string | null
}

/**
 * Query string sin claves vacías.
 *
 * Importa que una clave ausente no viaje como `&marca=`: el esquema del backend
 * acota `marca` a `min(1)`, así que una cadena vacía es un 400 de validación y
 * no "sin filtro".
 */
function cadenaConsulta(partes: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams()
  for (const [clave, valor] of Object.entries(partes)) {
    if (valor === undefined || valor === null || valor === '') continue
    p.set(clave, String(valor))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

/**
 * Tamaño de página del catálogo.
 *
 * 24 y no el máximo: la grilla es fluida de `minmax(228px, 1fr)`, así que 24
 * llena entre tres y seis filas en cualquier pantalla razonable, y la
 * paginación es un botón "Ver más" —no scroll infinito— para que el pie de
 * página siga siendo alcanzable y el navegador pueda volver al mismo lugar.
 */
const PAGINA_CATALOGO = 24
const PAGINA_ORDENES = 25

/**
 * Categorías con su contador, para la barra lateral.
 *
 * Cachea largo: es la navegación del catálogo, cambia cuando el equipo publica
 * una categoría nueva y no cada vez que alguien abre la vitrina.
 */
export function useCategorias(): UseQueryResult<CategoriaPublica[], ErrorApi> {
  return useQuery<CategoriaPublica[], ErrorApi>({
    queryKey: claves.categorias,
    queryFn: () => pedir<CategoriaPublica[]>('/api/categorias'),
    staleTime: 5 * 60_000,
  })
}

/**
 * El catálogo, paginado por cursor.
 *
 * El filtrado, la búsqueda y el orden los resuelve el SERVIDOR. En la v1 todo
 * eso ocurría en memoria del navegador sobre el catálogo completo, lo que
 * funcionaba con 79 productos y deja de funcionar mucho antes de lo que
 * parece: el precio de venta no es una columna y el costo no se publica, así
 * que ni el orden por precio se puede calcular acá sin que el navegador reciba
 * datos que no le corresponden.
 */
export function useCatalogo(
  filtros: FiltrosCatalogo,
): UseInfiniteQueryResult<InfiniteData<PaginaCatalogo>, ErrorApi> {
  return useInfiniteQuery<PaginaCatalogo, ErrorApi, InfiniteData<PaginaCatalogo>, readonly unknown[], string | null>({
    queryKey: claves.catalogo(filtros),
    queryFn: ({ pageParam }) =>
      pedir<PaginaCatalogo>(
        `/api/catalogo${cadenaConsulta({
          cursor: pageParam,
          limit: PAGINA_CATALOGO,
          categoria: filtros.categoria,
          marca: filtros.marca,
          proveedorId: filtros.proveedorId,
          q: filtros.q,
          orden: filtros.orden,
          faenaId: filtros.faenaId,
        })}`,
      ),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
  })
}

/**
 * Cuántas sugerencias muestra el buscador de la portada.
 *
 * Ocho es el techo de lo que se lee de un vistazo sin desplazar la lista. Más
 * no ayuda: quien no encuentra lo suyo entre ocho no lo va a encontrar entre
 * veinte, va a reescribir la búsqueda — y para eso está la última fila, que
 * lleva al catálogo con los filtros completos.
 */
export const SUGERENCIAS_MAXIMAS = 8

/**
 * Mínimo de caracteres antes de preguntarle al servidor.
 *
 * Con una sola letra la respuesta es el catálogo entero recortado a ocho filas
 * arbitrarias: ruido que además se pide en cada tecla. Dos es donde la
 * sugerencia empieza a decir algo.
 */
export const MINIMO_SUGERENCIA = 2

/**
 * Sugerencias mientras se escribe, para el buscador de la portada.
 *
 * Reutiliza `GET /api/catalogo` en vez de tener su propio endpoint, y es
 * deliberado: la búsqueda ya la resuelve el servidor con el índice FULLTEXT más
 * las coincidencias por código y por número de parte, y un endpoint aparte
 * significaría dos implementaciones de «qué calza con este texto» que tarde o
 * temprano dan resultados distintos para la misma palabra. Lo único propio es
 * el tope de filas.
 *
 * `placeholderData` conserva la lista anterior mientras llega la nueva: sin eso
 * el desplegable se vacía y vuelve a llenarse en cada tecla, y lo que se lee es
 * un parpadeo.
 */
export function useSugerencias(q: string): UseQueryResult<PaginaCatalogo, ErrorApi> {
  const texto = q.trim()
  return useQuery<PaginaCatalogo, ErrorApi>({
    queryKey: claves.sugerencias(texto),
    queryFn: () =>
      pedir<PaginaCatalogo>(
        `/api/catalogo${cadenaConsulta({ q: texto, limit: SUGERENCIAS_MAXIMAS })}`,
      ),
    enabled: texto.length >= MINIMO_SUGERENCIA,
    placeholderData: (previa) => previa,
    // Lo que hay publicado no cambia entre una tecla y la siguiente.
    staleTime: 60_000,
  })
}

/** Un producto. 404 si no es visible, que es lo mismo que no existir. */
export function useProducto(
  id: string,
  faenaId?: string | null,
): UseQueryResult<ItemCatalogo, ErrorApi> {
  return useQuery<ItemCatalogo, ErrorApi>({
    queryKey: claves.producto(id, faenaId),
    queryFn: () =>
      pedir<ItemCatalogo>(
        `/api/catalogo/${encodeURIComponent(id)}${cadenaConsulta({ faenaId: faenaId ?? undefined })}`,
      ),
    enabled: id !== '',
  })
}

/**
 * La documentación técnica del artículo, tal como la ve el cliente.
 *
 * Es PÚBLICA: un visitante sin sesión ve qué documentos hay. Descargarlos exige
 * entrar, y eso lo dice `descargable` en la misma respuesta, para poder ofrecer
 * "entrar para verla" en vez de un enlace que responde 401 en una pestaña
 * nueva.
 *
 * Cachea largo porque un datasheet cambia el día que el proveedor lo reemplaza,
 * no durante la visita.
 */
export function useDocumentosArticulo(
  articuloId: string,
): UseQueryResult<DocumentosArticulo, ErrorApi> {
  return useQuery<DocumentosArticulo, ErrorApi>({
    queryKey: claves.documentos(articuloId),
    queryFn: () =>
      pedir<DocumentosArticulo>(
        `/api/catalogo/${encodeURIComponent(articuloId)}/documentos`,
      ),
    enabled: articuloId !== '',
    staleTime: 10 * 60_000,
  })
}

/**
 * A dónde apunta el enlace de un documento.
 *
 * Se compone acá y no en el servidor porque no hay nada que firmar: el binario
 * lo sirve la API, precisamente para que la clave del objeto —que empieza por
 * el id del proveedor— no llegue nunca al navegador.
 */
export function urlDocumento(articuloId: string, documentoId: string): string {
  return `/api/catalogo/${encodeURIComponent(articuloId)}/documentos/${encodeURIComponent(documentoId)}`
}

/**
 * Las fichas de los productos que hay en el carrito.
 *
 * Una consulta por ARTÍCULO y no una lista: la cotización devuelve las CIFRAS
 * del carrito, no los nombres, y `/api/catalogo` no filtra por un conjunto de
 * ids. Con `useQueries` cada ficha se cachea por su cuenta y se comparte con la
 * pantalla de detalle, así que volver al carrito desde una ficha no vuelve a
 * pedir nada. Un artículo dado de baja responde 404 y su línea queda sin ficha:
 * la pantalla lo dice y ofrece quitarla, que es la misma señal que trae
 * `faltantes` en la cotización.
 *
 * Los ids son de ARTÍCULO. Es lo que guarda el carrito y lo único que el cliente
 * conoce: qué oferta se compra lo decide el servidor.
 */
/**
 * Avanza UNA línea de una orden.
 *
 * Lo usa el PROVEEDOR con sus propias líneas —el que despacha registra su propio
 * avance, sin que alguien más lo transcriba— y el equipo con cualquiera. La
 * invalidación tiene que alcanzar a la orden completa: al mover una línea, el
 * estado de la orden se recalcula como el de la línea menos avanzada, así que la
 * cabecera también cambió.
 */
export function useAvanzarLinea(): UseMutationResult<
  { estadoLinea: string; estadoOrden: string },
  ErrorApi,
  { ordenId: string; lineaId: string; estado: string; motivo?: string; cantidadDespachada?: number }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ordenId, lineaId, ...cuerpo }) =>
      pedir<{ estadoLinea: string; estadoOrden: string }>(
        `/api/ordenes/${encodeURIComponent(ordenId)}/lineas/${encodeURIComponent(lineaId)}/estado`,
        { metodo: 'PATCH', cuerpo },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.ordenes() })
      void qc.invalidateQueries({ queryKey: ['proveedor', 'ordenes'] })
    },
  })
}

/**
 * El equipo revisa la orden y le manda la propuesta al cliente.
 *
 * Solo viajan las líneas que CAMBIAN. Mandar las cuarenta de una orden para
 * tocar una obligaría al navegador a reconstruir el estado completo y al
 * servidor a confiar en esa reconstrucción, que es justo lo que este sistema no
 * hace con nada que venga del cliente.
 */
export function useProponerAjuste(
  ordenId: string,
): UseMutationResult<
  { aprobacionCliente: string; ronda: number; venceEn: string },
  ErrorApi,
  {
    nota: string
    fechaComprometida?: string
    lineas?: { lineaId: string; cantidad?: number; precioUnitario?: number }[]
  }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      pedir<{ aprobacionCliente: string; ronda: number; venceEn: string }>(
        `/api/ordenes/${encodeURIComponent(ordenId)}/propuesta`,
        { metodo: 'POST', cuerpo },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.orden(ordenId) })
      void qc.invalidateQueries({ queryKey: claves.ordenes() })
    },
  })
}

/**
 * El cliente aprueba o rechaza lo que se le propuso.
 *
 * Aprobar aplica los cambios: mueve stock si cambió la cantidad y recalcula los
 * totales con el flete incluido, así que se invalida también el catálogo — esas
 * unidades cambiaron de disponibilidad.
 */
export function useResponderPropuesta(
  ordenId: string,
): UseMutationResult<
  { aprobacionCliente: string; estado: string; ronda: number },
  ErrorApi,
  { respuesta: 'aprobada' } | { respuesta: 'rechazada'; motivo: string }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      pedir<{ aprobacionCliente: string; estado: string; ronda: number }>(
        `/api/ordenes/${encodeURIComponent(ordenId)}/propuesta/responder`,
        { metodo: 'POST', cuerpo },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.orden(ordenId) })
      void qc.invalidateQueries({ queryKey: claves.ordenes() })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/**
 * La respuesta del proveedor a la OC: acepta —con cuánto— o rechaza —con por qué—.
 *
 * Va aparte de `useAvanzarLinea` porque son dos hechos distintos: aceptar no
 * despacha nada, solo levanta la compuerta. Un rechazo SÍ cancela la línea y
 * libera su stock, así que la invalidación también alcanza al catálogo: esas
 * unidades vuelven a estar disponibles.
 */
export function useResponderLinea(): UseMutationResult<
  {
    respuesta: 'aceptada' | 'rechazada'
    cantidadConfirmada: number | null
    estadoLinea: string
    estadoOrden: string
  },
  ErrorApi,
  { ordenId: string; lineaId: string } & (
    | { respuesta: 'aceptada'; cantidadConfirmada?: number }
    | { respuesta: 'rechazada'; motivo: string }
  )
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ordenId, lineaId, ...cuerpo }) =>
      pedir<{
        respuesta: 'aceptada' | 'rechazada'
        cantidadConfirmada: number | null
        estadoLinea: string
        estadoOrden: string
      }>(
        `/api/ordenes/${encodeURIComponent(ordenId)}/lineas/${encodeURIComponent(lineaId)}/respuesta`,
        { metodo: 'PATCH', cuerpo },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.ordenes() })
      void qc.invalidateQueries({ queryKey: ['proveedor', 'ordenes'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

export function useProductosCarrito(ids: string[]): {
  porId: Map<string, ItemCatalogo>
  cargando: boolean
} {
  const consultas = useQueries({
    queries: ids.map((id) => ({
      queryKey: claves.producto(id),
      queryFn: () => pedir<ItemCatalogo>(`/api/catalogo/${encodeURIComponent(id)}`),
      staleTime: 60_000,
    })),
  })

  const porId = new Map<string, ItemCatalogo>()
  for (const c of consultas) {
    if (c.data) porId.set(c.data.id, c.data)
  }
  return { porId, cargando: consultas.some((c) => c.isPending) }
}

/**
 * Las faenas que uno puede elegir como destino.
 *
 * Un solo tiro con el tope del endpoint, igual que `useProveedores`: alimenta un
 * selector, y una minera no tiene doscientas faenas. El backend filtra por
 * alcance —un cliente ve las suyas— así que acá no se filtra nada.
 *
 * `habilitada` evita disparar la petición para un alcance que recibiría una
 * lista vacía o un 401.
 */
export function useFaenas(habilitada = true): UseQueryResult<Pagina<Faena>, ErrorApi> {
  return useQuery<Pagina<Faena>, ErrorApi>({
    queryKey: claves.faenas,
    queryFn: () => pedir<Pagina<Faena>>(`/api/faenas${cadenaConsulta({ limit: 200 })}`),
    enabled: habilitada,
    // Las da de alta el equipo, no el cliente: no cambian durante una compra.
    staleTime: 5 * 60_000,
  })
}

/**
 * Los operadores logísticos que uno puede elegir.
 *
 * El operador es el andén DONDE ENTREGAMOS: nuestro transporte llega a él y la
 * minera hace el último tramo hasta su faena. La lista sale de
 * `GET /api/operadores`, que devuelve los asignados al propio cliente — la
 * asignación es explícita porque el transporte a una faena minera se negocia
 * entre la minera y el transportista, y ofrecer uno sin contrato es ofrecer un
 * despacho que no se va a poder hacer.
 *
 * Puede venir VACÍA, y eso no es un error: un cliente sin operadores asignados
 * compra con el tarifario general, como se hizo siempre. La pantalla distingue
 * los dos casos.
 *
 * El backend devuelve el predeterminado primero, así que el orden que llega es
 * el que se muestra: la primera opción de un desplegable es la que se acepta
 * sin leer, y tiene que ser la que el equipo dejó marcada.
 */
export function useOperadores(
  habilitada = true,
): UseQueryResult<Pagina<OperadorElegible>, ErrorApi> {
  return useQuery<Pagina<OperadorElegible>, ErrorApi>({
    queryKey: claves.operadores,
    queryFn: () => pedir<Pagina<OperadorElegible>>('/api/operadores'),
    enabled: habilitada,
    // Los asigna el equipo, no el cliente: no cambian durante una compra.
    staleTime: 5 * 60_000,
  })
}

/**
 * La cotización del carrito. **La cifra vinculante.**
 *
 * Es una consulta y no una mutación aunque viaje por POST: no cambia nada en el
 * servidor, y como consulta gana caché por (faena, líneas) y un estado de
 * "recalculando" que no borra lo anterior de la pantalla. El POST es porque el
 * carrito completo no cabe en una query string.
 *
 * `placeholderData` conserva la cotización previa mientras llega la nueva: ver
 * el total desaparecer y reaparecer con cada tecla en el campo de cantidad es
 * peor que verlo un segundo desactualizado, sobre todo si el rótulo lo dice.
 */
export function useCotizacion(
  items: ItemPedidoDto[],
  faenaId: string | null,
  habilitada = true,
): UseQueryResult<Cotizacion, ErrorApi> {
  return useQuery<Cotizacion, ErrorApi>({
    queryKey: claves.cotizacion(faenaId, items),
    queryFn: () =>
      pedir<Cotizacion>('/api/carrito/cotizar', { metodo: 'POST', cuerpo: { faenaId, items } }),
    enabled: habilitada && items.length > 0,
    // Precios y stock los mueve el servidor: una cotización de hace cinco
    // minutos no sirve para confirmar una orden.
    staleTime: 15_000,
    placeholderData: (previa) => previa,
  })
}

/**
 * Emite la orden.
 *
 * Manda ÚNICAMENTE faena y líneas. Ninguna cifra: el servidor recalcula precio,
 * flete, IVA y plazo leyendo la base, y si acá se enviara un total el cliente
 * podría mandar el que quisiera.
 */
export function useEmitirOrden(): UseMutationResult<
  OrdenEmitida,
  ErrorApi,
  {
    faenaId: string
    items: ItemPedidoDto[]
    requirioCotizacion?: boolean
    /** Operador que retira. `null` cuando el cliente no tiene ninguno habilitado. */
    operadorLogisticoId?: string | null
  }
> {
  const qc = useQueryClient()
  return useMutation<
    OrdenEmitida,
    ErrorApi,
    {
    faenaId: string
    items: ItemPedidoDto[]
    requirioCotizacion?: boolean
    /** Operador que retira. `null` cuando el cliente no tiene ninguno habilitado. */
    operadorLogisticoId?: string | null
  }
  >({
    mutationFn: (datos) => pedir<OrdenEmitida>('/api/ordenes', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      // La orden nueva encabeza el listado, y la emisión reservó stock: el
      // catálogo que quedó en caché puede estar mostrando disponibilidad vieja.
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/** Las órdenes que a uno le corresponden, paginadas por cursor. */
export function useOrdenes(
  estado?: EstadoOrdenDto,
): UseInfiniteQueryResult<InfiniteData<Pagina<Orden>>, ErrorApi> {
  return useInfiniteQuery<Pagina<Orden>, ErrorApi, InfiniteData<Pagina<Orden>>, readonly unknown[], string | null>({
    queryKey: claves.ordenes(estado),
    queryFn: ({ pageParam }) =>
      pedir<Pagina<Orden>>(
        `/api/ordenes${cadenaConsulta({ cursor: pageParam, limit: PAGINA_ORDENES, estado })}`,
      ),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
  })
}

/** Una orden con su bitácora de estados. */
export function useOrden(id: string): UseQueryResult<OrdenConEventos, ErrorApi> {
  return useQuery<OrdenConEventos, ErrorApi>({
    queryKey: claves.orden(id),
    queryFn: () => pedir<OrdenConEventos>(`/api/ordenes/${encodeURIComponent(id)}`),
    enabled: id !== '',
  })
}

/**
 * Las líneas de una orden, en su propia petición.
 *
 * Aparte del listado a propósito: es el defecto 1 de la v1, donde el navegador
 * traía TODAS las líneas de la base sin filtro y, al chocar contra el límite de
 * filas, recibía un subconjunto en silencio.
 */
export function useLineasOrden(id: string): UseQueryResult<Pagina<LineaOrden>, ErrorApi> {
  return useQuery<Pagina<LineaOrden>, ErrorApi>({
    queryKey: claves.lineasOrden(id),
    queryFn: () => pedir<Pagina<LineaOrden>>(`/api/ordenes/${encodeURIComponent(id)}/lineas`),
    enabled: id !== '',
  })
}

/**
 * Cancela una orden.
 *
 * El motivo es obligatorio y lo exige el backend. Quién puede y desde qué
 * estado también lo decide el backend: un cliente solo cancela la suya y solo
 * antes de que entre en preparación. La interfaz esconde el botón cuando no
 * corresponde, que es una cortesía, no un control.
 */
export function useCancelarOrden(
  id: string,
): UseMutationResult<{ estado: EstadoOrdenDto }, ErrorApi, { motivo: string }> {
  const qc = useQueryClient()
  return useMutation<{ estado: EstadoOrdenDto }, ErrorApi, { motivo: string }>({
    mutationFn: (datos) =>
      pedir<{ estado: EstadoOrdenDto }>(`/api/ordenes/${encodeURIComponent(id)}/cancelar`, {
        metodo: 'POST',
        cuerpo: datos,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.orden(id) })
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
      // Cancelar libera el stock reservado.
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Panel de proveedor, importación y archivos
 *
 * Los tipos de ENTRADA vienen de `@compranorte/contracts`
 * (`EntradaPublicarProducto`, `EntradaMovimientoProveedor`,
 * `EntradaImportarCatalogo`): si mañana el esquema pide un campo más, esto deja
 * de compilar en vez de mandar un cuerpo que el backend rechaza en producción.
 *
 * Las respuestas NO están en `contracts` —igual que las de órdenes— porque las
 * arma el módulo del servidor campo por campo según quién pregunta, y ahí está
 * justamente lo que importa: a un proveedor NO le llegan `precioVentaNeto` ni
 * `margenOverride`. Se declaran acá, en el borde, con esos dos campos como
 * OPCIONALES: así el compilador obliga a comprobar antes de pintarlos y no hay
 * forma de que una pantalla los muestre suponiendo que siempre vienen.
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Un producto tal como lo ve quien administra el catálogo.
 *
 * Los campos opcionales no son "por si acaso": son exactamente los que el
 * backend omite según el alcance. `costoNeto` y los dos saldos llegan al dueño
 * y al equipo; `precioVentaNeto` y `margenOverride`, SOLO al equipo, porque el
 * proveedor ya ve su costo y con el precio delante el margen sale de una
 * división.
 */
export interface ItemPanel {
  id: string
  sku: string
  nombre: string
  descripcion: string
  marca: string
  mpn: string | null
  marcaFabricante: string | null
  imagenUrl: string | null
  categoriaId: string
  categoriaSlug: string
  categoriaNombre: string
  pesoKg: number
  largoCm: number
  anchoCm: number
  altoCm: number
  activo: boolean
  hayStock: boolean
  /** CLP neto. **Solo equipo.** */
  precioVentaNeto?: number
  stockDisponible?: number
  /** Saldo del libro. Llega al dueño y al equipo. */
  stockActual?: number
  /** Comprometido por órdenes vivas. Llega al dueño y al equipo. */
  stockReservado?: number
  proveedorId?: string
  costoNeto?: number
  /** Fracción; `null` es "sin override". **Solo equipo.** */
  margenOverride?: number | null
  stockActualizadoEn?: string
}

/** Una línea de orden vista por su proveedor: al costo, nunca al precio. */
export interface LineaOrdenProveedor {
  id: string
  productoId: string
  sku: string
  nombre: string
  marca: string
  cantidad: number
  cantidadDespachada: number
  estadoLinea: EstadoOrdenDto
  /** Si se comprometió a surtirla. Es una pregunta distinta a `estadoLinea`. */
  respuestaProveedor: RespuestaProveedorDto
  /** Cuántas confirmó que puede surtir. Puede ser menor a lo pedido. */
  cantidadConfirmada: number | null
  /** Por qué no puede. Solo lo ven el equipo y el proveedor que lo escribió. */
  motivoRechazo: string | null
  respondidaEn: string | null
  /** Lo que Compranorte le paga por unidad. */
  costoUnitario: number
  /** **Solo equipo.** */
  precioUnitario?: number
}

/**
 * Una orden que toca sus productos.
 *
 * Trae la faena de destino y la fecha comprometida —lo que hace falta para
 * despachar— y NO trae las cifras de la orden: de cualquiera de ellas se
 * despeja el margen. Solo al equipo le llegan `subtotalNeto`, `flete`, `iva` y
 * `total`.
 */
export interface OrdenProveedor {
  id: string
  numero: string
  fecha: string
  estado: EstadoOrdenDto
  modoDespacho: ModoDespacho
  fechaComprometida: string | null
  entregadaEn: string | null
  clienteRazonSocial: string | null
  faenaNombre: string | null
  faenaCiudad: string | null
  faenaRegion: string | null
  lineas: LineaOrdenProveedor[]
  /**
   * Su orden de compra parcial: el número que pone en la factura.
   *
   * `null` mientras la orden no se confirme — la OC sale al confirmar, que es
   * cuando Compranorte se compromete a comprar.
   */
  ordenCompra: string | null
  /** `null` si todavía no salió el aviso: por ejemplo, sin correo cargado. */
  ordenCompraEnviadaEn: string | null
  /** Lo que Compranorte le va a pagar por esta orden, a su costo. */
  costoTotal: number
  subtotalNeto?: number
  flete?: number
  iva?: number
  total?: number
}

/**
 * Lo mínimo de la ficha de proveedor que necesita la cabecera del panel.
 *
 * Es un subconjunto del `ProveedorAdmin` de `hooksAdmin`, declarado aparte a
 * propósito: importar ese módulo desde el panel de proveedor arrastraría los
 * treinta hooks de administración al mismo trozo de código, y un proveedor no
 * tiene por qué descargarlos. El estado y la ciudad se derivan de `contracts`,
 * así que si el enum de la base gana un valor esto deja de compilar.
 */
export interface ProveedorPanel {
  id: string
  razonSocial: string
  rut: string
  ciudadOrigen: CiudadOrigenDto
  estado: EstadoProveedorDto
  /** Cuántos productos tiene cargados. Solo viene en el listado. */
  productos?: number
}

/** Acuse de un movimiento de stock: el saldo QUE QUEDÓ, no el que se pidió. */
export interface RespuestaMovimiento {
  productoId: string
  tipo: TipoMovimientoProveedor
  cantidad: number
  stockActual: number
  stockReservado: number
  stockDisponible: number
  stockActualizadoEn: string
}

/** Metadatos de un archivo ya registrado. El binario nunca pasa por la API. */
export interface ArchivoGuardado {
  id: string
  tipo: TipoArchivo
  nombreOriginal: string
  mime: string
  bytes: number
  publico: boolean
  anchoPx: number | null
  altoPx: number | null
  creadoEn: string
  productoId: string | null
  /**
   * Si se muestra en la ficha pública del artículo.
   *
   * NO es lo mismo que `publico`, que dice en qué bucket vive el binario. Una
   * ficha técnica vive en el privado —su URL firmada lleva el id del proveedor
   * en la ruta— y aun así se le muestra al cliente, servida por la API.
   */
  visibleCatalogo: boolean
}

/** URL con la que se abre un archivo. Las privadas vienen firmadas y caducan. */
export interface EnlaceArchivo {
  url: string
  nombre: string
  /** `null` en las públicas: no caducan. */
  expiraEnSegundos: number | null
}

const PAGINA_PANEL = 50

/**
 * Configuración pública del entorno.
 *
 * La necesita la pantalla de documentos: cuando `archivosHabilitados` es falso
 * los endpoints de archivos responden 503, y hay que decirlo con calma ANTES de
 * que la persona elija un archivo, no después de que la subida falle. Cachea
 * larguísimo porque cambia cuando cambia el despliegue, no durante la sesión.
 */
export function useConfigPublica(): UseQueryResult<ConfigPublica, ErrorApi> {
  return useQuery<ConfigPublica, ErrorApi>({
    queryKey: claves.config,
    queryFn: () => pedir<ConfigPublica>('/api/config'),
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Fichas de proveedor, para el selector del equipo.
 *
 * `habilitada` es la que decide, y se le pasa `esEquipo`: un proveedor opera
 * sobre el suyo y no elige, así que ni siquiera se dispara la petición —que
 * además le respondería 403—.
 */
export function useProveedores(
  habilitada: boolean,
): UseQueryResult<Pagina<ProveedorPanel>, ErrorApi> {
  return useQuery<Pagina<ProveedorPanel>, ErrorApi>({
    queryKey: claves.proveedores,
    queryFn: () =>
      pedir<Pagina<ProveedorPanel>>(`/api/admin/proveedores${cadenaConsulta({ limit: 200 })}`),
    enabled: habilitada,
    staleTime: 5 * 60_000,
  })
}

/**
 * El catálogo propio, paginado por cursor.
 *
 * `proveedorId` viaja en la CONSULTA y solo lo usa el equipo; para un proveedor
 * va en `null` y el backend lo resuelve desde el token. Nunca en el cuerpo: en
 * la v1 el dueño del producto se tomaba del formulario y se podía publicar a
 * nombre de la competencia.
 *
 * `habilitada` existe porque para el EQUIPO el proveedor es obligatorio —
 * `proveedorObjetivo` responde 400 "Falta indicar el proveedor" si no viene—,
 * así que la pantalla no debe disparar la consulta hasta que haya elegido uno.
 */
export function useProductosProveedor(
  proveedorId: string | null,
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<ItemPanel>>, ErrorApi> {
  return useInfiniteQuery<
    Pagina<ItemPanel>,
    ErrorApi,
    InfiniteData<Pagina<ItemPanel>>,
    readonly unknown[],
    string | null
  >({
    queryKey: claves.panelProductos(proveedorId),
    queryFn: ({ pageParam }) =>
      pedir<Pagina<ItemPanel>>(
        `/api/proveedor/productos${cadenaConsulta({
          cursor: pageParam,
          limit: PAGINA_PANEL,
          proveedorId,
        })}`,
      ),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
    enabled: habilitada,
  })
}

/** Órdenes que tocan sus productos, paginadas. */
export function useOrdenesProveedor(
  proveedorId: string | null,
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<OrdenProveedor>>, ErrorApi> {
  return useInfiniteQuery<
    Pagina<OrdenProveedor>,
    ErrorApi,
    InfiniteData<Pagina<OrdenProveedor>>,
    readonly unknown[],
    string | null
  >({
    queryKey: claves.panelOrdenes(proveedorId),
    queryFn: ({ pageParam }) =>
      pedir<Pagina<OrdenProveedor>>(
        `/api/proveedor/ordenes${cadenaConsulta({
          cursor: pageParam,
          limit: PAGINA_PANEL,
          proveedorId,
        })}`,
      ),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
    enabled: habilitada,
  })
}

/**
 * Publica un producto.
 *
 * El cuerpo NO lleva `proveedorId` —el esquema es `strict()`, así que mandarlo
 * es un 400— y tampoco `margenOverride`: el precio de venta lo fija Compra
 * Norte. El stock inicial va como dato del formulario, pero el backend no
 * escribe la columna: lleva el saldo a ese valor con un movimiento en el libro.
 *
 * Invalida el catálogo público además del panel: un producto nuevo y activo
 * aparece en la vitrina, y el contador de su categoría sube.
 */
export function usePublicarProducto(
  proveedorId: string | null,
): UseMutationResult<ItemPanel, ErrorApi, EntradaPublicarProducto> {
  const qc = useQueryClient()
  return useMutation<ItemPanel, ErrorApi, EntradaPublicarProducto>({
    mutationFn: (datos) =>
      pedir<ItemPanel>(`/api/proveedor/productos${cadenaConsulta({ proveedorId })}`, {
        metodo: 'POST',
        cuerpo: datos,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      void qc.invalidateQueries({ queryKey: claves.categorias })
    },
  })
}

/**
 * Edita un producto. Es el camino del chip publicado/pausado.
 *
 * Lo que NO es editable por acá: el SKU —identidad del producto y clave de la
 * importación—, el proveedor dueño y los saldos de stock, que se mueven solo
 * por el libro de movimientos.
 */
export function useEditarProducto(): UseMutationResult<
  ItemPanel,
  ErrorApi,
  { id: string; cambios: EntradaEditarProducto }
> {
  const qc = useQueryClient()
  return useMutation<ItemPanel, ErrorApi, { id: string; cambios: EntradaEditarProducto }>({
    mutationFn: ({ id, cambios }) =>
      pedir<ItemPanel>(`/api/proveedor/productos/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/**
 * Registra un movimiento de stock.
 *
 * Solo `ingreso` y `ajuste`, y eso lo impone el esquema compartido: `reserva`,
 * `liberacion` y `salida` las emite el sistema al mover las órdenes. En la v1
 * un proveedor podía insertar una `liberacion`, soltar las reservas de órdenes
 * vivas y vender dos veces la misma unidad.
 *
 * Invalida el catálogo porque el stock decide la disponibilidad publicada.
 */
export function useMovimientoStock(): UseMutationResult<
  RespuestaMovimiento,
  ErrorApi,
  EntradaMovimientoProveedor
> {
  const qc = useQueryClient()
  return useMutation<RespuestaMovimiento, ErrorApi, EntradaMovimientoProveedor>({
    mutationFn: (datos) =>
      pedir<RespuestaMovimiento>('/api/proveedor/movimientos', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/**
 * Importación masiva de catálogo.
 *
 * Manda las filas ya parseadas: el parser del dominio corre en el NAVEGADOR
 * para dar respuesta inmediata sobre un archivo de miles de líneas, y el
 * servidor revalida cada fila con el mismo esquema. La carga entera va en una
 * transacción, así que o entran todas o no entra ninguna.
 */
export function useImportarCatalogo(): UseMutationResult<
  ResultadoImportacion,
  ErrorApi,
  EntradaImportarCatalogo
> {
  const qc = useQueryClient()
  return useMutation<ResultadoImportacion, ErrorApi, EntradaImportarCatalogo>({
    mutationFn: (datos) =>
      pedir<ResultadoImportacion>('/api/proveedor/importar', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      void qc.invalidateQueries({ queryKey: claves.categorias })
    },
  })
}

/* ─── Archivos ───────────────────────────────────────────────────────────────
 *
 * La subida es de TRES pasos y el binario no pasa por la API: una foto de 8 MB
 * atravesando el servidor sería lenta, cara y chocaría con los límites de la
 * plataforma.
 *
 *   1. `firmar` devuelve una URL prefirmada, las cabeceras exactas y un BOLETO;
 *   2. el navegador hace `PUT` a esa URL con esas cabeceras y nada más;
 *   3. `confirmar` registra el archivo presentando el BOLETO.
 *
 * El paso 3 nunca manda la clave. En la v1 `confirmar` aceptaba la clave del
 * cliente sin comprobar de quién era, y como la rama de error borraba el objeto
 * en esa clave, cualquier usuario autenticado podía eliminar archivos de otro
 * proveedor.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Lo que devuelve `firmar`. `clave` se informa para depurar y no se reenvía. */
interface Firma {
  url: string
  cabeceras: Record<string, string>
  boleto: string
  expiraEnSegundos: number
  clave: string
}

/** Lo que devuelve `confirmar`: el tamaño y el tipo REALES leídos del almacén. */
export interface ArchivoConfirmado {
  id: string
  clave: string
  tipo: TipoArchivo
  bytes: number
  mime: string
  /** Solo las fotos tienen URL directa; la documentación se sirve firmada. */
  url: string | null
}

/** Etapa en curso, para que el rótulo del botón diga en qué va la subida. */
export type EtapaSubida = 'firmando' | 'subiendo' | 'confirmando'

export interface EntradaSubida {
  archivo: File
  tipo: TipoArchivo
  productoId: string
  alProgresar?: (etapa: EtapaSubida) => void
}

/** Los archivos de un producto. */
export function useArchivosProducto(
  productoId: string,
): UseQueryResult<Pagina<ArchivoGuardado>, ErrorApi> {
  return useQuery<Pagina<ArchivoGuardado>, ErrorApi>({
    queryKey: claves.archivos(productoId),
    queryFn: () => pedir<Pagina<ArchivoGuardado>>(`/api/archivos${cadenaConsulta({ productoId })}`),
    enabled: productoId !== '',
  })
}

/**
 * Sube un archivo: firmar → PUT → confirmar.
 *
 * El `PUT` va por `fetch` pelado y no por `pedir`: es OTRO origen —el almacén—,
 * así que no lleva nuestras cookies, y sus cabeceras tienen que ser EXACTAMENTE
 * las que devolvió `firmar`, porque son las que quedaron dentro de la firma.
 * Agregar una sola, incluido un `Accept`, la invalida y el almacén responde 403.
 */
export function useSubirArchivo(): UseMutationResult<ArchivoConfirmado, ErrorApi, EntradaSubida> {
  const qc = useQueryClient()
  return useMutation<ArchivoConfirmado, ErrorApi, EntradaSubida>({
    mutationFn: async ({ archivo, tipo, productoId, alProgresar }) => {
      alProgresar?.('firmando')
      const firma = await pedir<Firma>('/api/archivos/firmar', {
        metodo: 'POST',
        cuerpo: {
          tipo,
          mime: archivo.type,
          bytes: archivo.size,
          nombre: archivo.name,
          productoId,
        },
      })

      alProgresar?.('subiendo')
      let respuesta: Response
      try {
        respuesta = await fetch(firma.url, {
          method: 'PUT',
          headers: firma.cabeceras,
          body: archivo,
        })
      } catch {
        /*
         * Un fallo de red acá suele ser el CORS del bucket, no el archivo. Se
         * dice así para que quien lo lea no salga a comprimir su PDF en vano.
         */
        throw new ErrorApi(
          'no_disponible',
          'No se pudo alcanzar el almacenamiento. Revise su conexión e intente de nuevo.',
          0,
        )
      }
      if (!respuesta.ok) {
        throw new ErrorApi(
          'no_disponible',
          `El almacenamiento rechazó la subida (HTTP ${respuesta.status}). Vuelva a intentarlo.`,
          respuesta.status,
        )
      }

      alProgresar?.('confirmando')
      // El BOLETO, nunca la clave: es lo que impide apuntar la confirmación a
      // un archivo ajeno.
      return pedir<ArchivoConfirmado>('/api/archivos/confirmar', {
        metodo: 'POST',
        cuerpo: { boleto: firma.boleto, nombre: archivo.name },
      })
    },
    onSuccess: (_datos, entrada) => {
      void qc.invalidateQueries({ queryKey: claves.archivos(entrada.productoId) })
      // La primera foto pública pasa a ser la portada del producto.
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
    },
  })
}

/**
 * Pide la URL con la que se abre un archivo.
 *
 * Es una mutación aunque sea un GET: la URL de un documento privado viene
 * firmada y caduca en diez minutos, así que no se cachea —una URL vencida en
 * caché es un enlace roto— y se pide en el momento en que la persona hace clic.
 */
export function useUrlArchivo(): UseMutationResult<EnlaceArchivo, ErrorApi, string> {
  return useMutation<EnlaceArchivo, ErrorApi, string>({
    mutationFn: (id) => pedir<EnlaceArchivo>(`/api/archivos/${encodeURIComponent(id)}/url`),
  })
}

/**
 * Publica u oculta un documento en la ficha del artículo.
 *
 * Los dos alcances no son simétricos y el backend lo impone: el equipo puede en
 * los dos sentidos, el proveedor dueño solo puede OCULTAR lo suyo. Publicar es
 * decisión del equipo porque es quien mira el PDF antes, y quien responde si
 * sale con el membrete de un distribuidor —que es exactamente lo que el cliente
 * no debe ver—.
 *
 * Invalida las DOS listas: la del proveedor y la que ve el cliente en la ficha.
 */
export function useVisibilidadArchivo(
  productoId: string,
): UseMutationResult<void, ErrorApi, { id: string; visibleCatalogo: boolean }> {
  const qc = useQueryClient()
  return useMutation<void, ErrorApi, { id: string; visibleCatalogo: boolean }>({
    mutationFn: ({ id, visibleCatalogo }) =>
      pedir<void>(`/api/archivos/${encodeURIComponent(id)}/visibilidad`, {
        metodo: 'PATCH',
        cuerpo: { visibleCatalogo },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.archivos(productoId) })
      void qc.invalidateQueries({ queryKey: ['documentos'] })
    },
  })
}

/**
 * Borra un archivo. Responde 204, sin cuerpo.
 *
 * Solo el equipo y el proveedor dueño alcanzan a borrar, y eso lo comprueba el
 * backend: acá la confirmación de dos clics es una cortesía contra el error, no
 * un control de acceso.
 */
export function useBorrarArchivo(productoId: string): UseMutationResult<void, ErrorApi, string> {
  const qc = useQueryClient()
  return useMutation<void, ErrorApi, string>({
    mutationFn: (id) =>
      pedir<void>(`/api/archivos/${encodeURIComponent(id)}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.archivos(productoId) })
      void qc.invalidateQueries({ queryKey: ['panel', 'productos'] })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      // También la documentación que ve el cliente: el archivo que se borró
      // puede estar publicado en la ficha del artículo.
      void qc.invalidateQueries({ queryKey: ['documentos'] })
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Registrar despacho parcial. Solo el equipo.
 *
 * Es lo que convierte el fill rate en un dato. En la v1 `cantidadDespachada`
 * solo la escribía el trigger de entrega, igualándola a lo pedido, así que la
 * métrica siempre daba 1,00 — una constante que parecía un indicador. Y sin
 * pantalla que escriba este campo, el 0 % del tablero es honesto pero inútil.
 */
export function useRegistrarDespacho(
  ordenId: string,
): UseMutationResult<{ actualizadas: number }, ErrorApi, { lineaId: string; cantidadDespachada: number }[]> {
  const qc = useQueryClient()
  return useMutation<
    { actualizadas: number },
    ErrorApi,
    { lineaId: string; cantidadDespachada: number }[]
  >({
    mutationFn: (lineas) =>
      pedir<{ actualizadas: number }>(`/api/ordenes/${ordenId}/despacho`, {
        metodo: 'PATCH',
        cuerpo: { lineas },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.lineasOrden(ordenId) })
      void qc.invalidateQueries({ queryKey: claves.orden(ordenId) })
      // El tablero de KPI depende de esto: su fill rate cambia al registrar.
      void qc.invalidateQueries({ queryKey: ['kpi'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Demanda no cubierta
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Una solicitud de catálogo tal como la proyecta `modulos/solicitudes.ts` para
 * quien NO es del equipo.
 *
 * `busqueda` y `detalle` llegan separados aunque en la base viajen en una sola
 * columna: el corte lo hace el servidor. La nota interna no está en el tipo
 * porque no viaja —la propiedad no existe en la respuesta—, y tenerla acá como
 * opcional invitaría a pintarla en una pantalla de cliente.
 */
export interface SolicitudPropia {
  id: string
  busqueda: string
  detalle: string | null
  cantidad: number | null
  estado: EstadoSolicitudDto
  faenaId: string | null
  faenaNombre: string | null
  creadoEn: string
}

/**
 * Registra que buscamos algo y no lo teníamos.
 *
 * ES EL ÚNICO CAMINO por el que una búsqueda sin resultados deja huella. Antes
 * de esto, quien no encontraba lo que necesitaba veía una lista vacía y se iba:
 * la demanda existía y el sistema no se enteraba nunca.
 *
 * No manda `clienteId`: el backend lo saca del token y el esquema lo rechazaría.
 * La faena viaja solo si el carrito ya tiene una en contexto, y una faena ajena
 * responde 404, así que no hay nada que adivinar acá.
 */
export function useCrearSolicitud(): UseMutationResult<
  SolicitudPropia,
  ErrorApi,
  EntradaCrearSolicitud
> {
  const qc = useQueryClient()
  return useMutation<SolicitudPropia, ErrorApi, EntradaCrearSolicitud>({
    mutationFn: (datos) =>
      pedir<SolicitudPropia>('/api/solicitudes', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.solicitudes })
      // La cola del equipo tiene una fila más, y el informe de frecuentes
      // cambió: si alguien del equipo pidió desde el catálogo, su panel
      // quedaría mostrando el recuento anterior.
      void qc.invalidateQueries({ queryKey: ['admin', 'solicitudes'] })
    },
  })
}

/** Lo que uno mismo pidió, paginado por cursor como todo listado. */
export function useSolicitudesPropias(
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<SolicitudPropia>>, ErrorApi> {
  return useInfiniteQuery<
    Pagina<SolicitudPropia>,
    ErrorApi,
    InfiniteData<Pagina<SolicitudPropia>>,
    readonly unknown[],
    string | null
  >({
    queryKey: claves.solicitudes,
    queryFn: ({ pageParam }) =>
      pedir<Pagina<SolicitudPropia>>(
        `/api/solicitudes${cadenaConsulta({ cursor: pageParam, limit: PAGINA_ORDENES })}`,
      ),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
    enabled: habilitada,
  })
}


/* ─── Cuentas de la propia empresa ────────────────────────────────────────────
 *
 * Lo que un cliente o un proveedor administra de SU empresa: quién opera, quién
 * solo mira y quién está activo. Es distinto de `/api/admin/perfiles`, que es de
 * Compra Norte y además puede mover una cuenta de una empresa a otra.
 *
 * Solo el nivel `administrador` alcanza estos endpoints; el resto recibe 403.
 * La interfaz esconde la pestaña, que es una cortesía y no un control.
 * ─────────────────────────────────────────────────────────────────────────── */

export function useCuentasEmpresa(
  habilitada = true,
): UseQueryResult<Pagina<CuentaEmpresa>, ErrorApi> {
  return useQuery<Pagina<CuentaEmpresa>, ErrorApi>({
    queryKey: claves.cuentasEmpresa,
    queryFn: () => pedir<Pagina<CuentaEmpresa>>('/api/empresa/cuentas'),
    enabled: habilitada,
  })
}

/**
 * Cambia el nivel o el estado de una cuenta de la empresa.
 *
 * Invalida además el perfil propio: un administrador que se degrada a sí mismo
 * pierde la pestaña en la misma pantalla, y sin esto seguiría viéndola hasta
 * recargar —con todos sus botones respondiendo 403—.
 */
export function useCambiarCuentaEmpresa(): UseMutationResult<
  CuentaEmpresa,
  ErrorApi,
  { id: string; cambios: EntradaCambiarCuenta }
> {
  const qc = useQueryClient()
  return useMutation<CuentaEmpresa, ErrorApi, { id: string; cambios: EntradaCambiarCuenta }>({
    mutationFn: ({ id, cambios }) =>
      pedir<CuentaEmpresa>(`/api/empresa/cuentas/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.cuentasEmpresa })
      void qc.invalidateQueries({ queryKey: claves.yo })
    },
  })
}


/* ─── La campana ──────────────────────────────────────────────────────────────
 *
 * Qué pasó con las órdenes que a uno le tocan. El servidor devuelve el listado
 * ya proyectado por alcance —incluido A DÓNDE lleva cada una al pulsarla— así
 * que acá no hay ninguna regla de negocio: solo la consulta y su refresco.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Cada cuánto se vuelve a preguntar por novedades, en milisegundos. */
const REFRESCO_NOTIFICACIONES = 60_000

export function useNotificaciones(
  habilitada = true,
): UseQueryResult<PaginaNotificaciones, ErrorApi> {
  return useQuery<PaginaNotificaciones, ErrorApi>({
    queryKey: claves.notificaciones,
    queryFn: () => pedir<PaginaNotificaciones>('/api/notificaciones'),
    enabled: habilitada,
    /*
     * Se refresca sola cada minuto, y es la ÚNICA consulta de la aplicación que
     * lo hace. Una campana que solo se actualiza al recargar no es una campana:
     * el estado de una orden cambia mientras la persona tiene la pestaña
     * abierta, que es justamente cuando quiere enterarse.
     *
     * Un minuto es lento a propósito. Estos hechos los produce una persona
     * —alguien confirma, alguien despacha— así que no ocurren cada segundo, y
     * bajar el intervalo solo multiplica consultas para adelantar un aviso que
     * nadie está mirando el reloj para recibir.
     */
    refetchInterval: REFRESCO_NOTIFICACIONES,
    /*
     * Y también al volver a la pestaña: quien estuvo en otra ventana media hora
     * espera encontrar la campana al día apenas vuelve, no un minuto después.
     */
    refetchOnWindowFocus: true,
  })
}

/**
 * Marca todo como leído.
 *
 * Actualiza la caché sin esperar la respuesta: el globo tiene que apagarse en el
 * mismo gesto que abre la campana. Si el servidor falla, el refresco siguiente
 * lo devuelve a su estado real — y equivocarse hacia «ya lo vio» es preferible a
 * un contador que se queda encendido después de leerlo.
 */
export function useMarcarNotificacionesLeidas(): UseMutationResult<unknown, ErrorApi, void> {
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, void>({
    mutationFn: () => pedir('/api/notificaciones/leidas', { metodo: 'POST' }),
    onMutate: () => {
      qc.setQueryData<PaginaNotificaciones>(claves.notificaciones, (previa) =>
        previa
          ? {
              ...previa,
              noLeidas: 0,
              saturado: false,
              datos: previa.datos.map((n) => ({ ...n, noLeida: false })),
            }
          : previa,
      )
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: claves.notificaciones })
    },
  })
}


/* ─── El asistente ────────────────────────────────────────────────────────────
 *
 * Qué ofrecerle a quien buscó algo y no lo encontró. El servidor relaja la
 * búsqueda con las reglas del dominio y prueba cada variante CONTRA EL CATÁLOGO
 * REAL, así que lo que vuelve son artículos que existen. Acá no hay ninguna
 * regla: solo la consulta.
 * ─────────────────────────────────────────────────────────────────────────── */

export function useAsistente(
  q: string,
  habilitada = true,
): UseQueryResult<SugerenciaCatalogo, ErrorApi> {
  const texto = q.trim()
  return useQuery<SugerenciaCatalogo, ErrorApi>({
    queryKey: claves.asistente(texto),
    queryFn: () => pedir<SugerenciaCatalogo>(`/api/catalogo/asistente${cadenaConsulta({ q: texto })}`),
    enabled: habilitada && texto.length >= MINIMO_SUGERENCIA,
    // Lo publicado no cambia entre una pregunta y la siguiente de la misma
    // conversación; y volver sobre lo mismo no debe costar otra consulta.
    staleTime: 5 * 60_000,
  })
}
